import React, { useState, useRef, useEffect } from 'react';
import { Palette, Check, Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const ThemeSwitcher = ({ collapsed = false, variant = 'default' }) => {
  const { theme, setTheme, themes, currentTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleThemeSelect = (themeId) => {
    setTheme(themeId);
    setIsOpen(false);
  };

  // Navbar variant - compact icon button
  if (variant === 'navbar') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title={`Theme: ${currentTheme.name}`}
        >
          {theme === 'light' ? (
            <Sun className="w-5 h-5 text-amber-500" />
          ) : (
            <Moon className="w-5 h-5 text-indigo-400" />
          )}
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute right-0 top-full mt-2 z-50 bg-white border border-gray-200 rounded-xl shadow-xl py-2 min-w-[200px]">
            <div className="px-3 py-2 border-b border-gray-100">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Choose Theme
              </span>
            </div>
            
            {themes.map((t) => (
              <button
                key={t.id}
                onClick={() => handleThemeSelect(t.id)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5
                  hover:bg-gray-50 transition-colors
                  ${theme === t.id ? 'bg-amber-50' : ''}
                `}
              >
                {/* Color Preview */}
                <div className="flex items-center gap-1">
                  <div 
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: t.preview.bg }}
                  />
                  <div 
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: t.preview.card }}
                  />
                  <div 
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: t.preview.accent }}
                  />
                </div>
                
                {/* Theme Name */}
                <span className="flex-1 text-left text-sm text-gray-700 font-medium">
                  {t.icon} {t.name}
                </span>
                
                {/* Check mark for selected */}
                {theme === t.id && (
                  <Check className="w-4 h-4 text-amber-500" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default/Sidebar variant
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Theme Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 w-full px-3 py-2.5 rounded-lg
          bg-theme-tertiary hover:bg-theme-accent/20
          text-theme-secondary hover:text-theme-primary
          transition-all duration-200
          ${collapsed ? 'justify-center' : 'justify-between'}
        `}
        title={collapsed ? `Theme: ${currentTheme.name}` : undefined}
      >
        <div className="flex items-center gap-2">
          <div 
            className="w-5 h-5 rounded-full border-2 border-theme"
            style={{ backgroundColor: currentTheme.preview.bg }}
          />
          {!collapsed && (
            <span className="text-sm font-medium">{currentTheme.name}</span>
          )}
        </div>
        {!collapsed && (
          <Palette className="w-4 h-4" />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className={`
            absolute z-50 bg-theme-secondary border border-theme rounded-lg shadow-theme-lg
            py-2 min-w-[180px]
            ${collapsed ? 'left-full ml-2 bottom-0' : 'bottom-full mb-2 left-0 right-0'}
          `}
        >
          <div className="px-3 py-1.5 border-b border-theme mb-1">
            <span className="text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Choose Theme
            </span>
          </div>
          
          {themes.map((t) => (
            <button
              key={t.id}
              onClick={() => handleThemeSelect(t.id)}
              className={`
                w-full flex items-center gap-3 px-3 py-2
                hover:bg-theme-tertiary transition-colors
                ${theme === t.id ? 'bg-theme-tertiary' : ''}
              `}
            >
              {/* Color Preview */}
              <div className="flex items-center gap-1">
                <div 
                  className="w-4 h-4 rounded-full border border-gray-400"
                  style={{ backgroundColor: t.preview.bg }}
                  title="Background"
                />
                <div 
                  className="w-4 h-4 rounded-full border border-gray-400"
                  style={{ backgroundColor: t.preview.card }}
                  title="Card"
                />
                <div 
                  className="w-4 h-4 rounded-full border border-gray-400"
                  style={{ backgroundColor: t.preview.accent }}
                  title="Accent"
                />
              </div>
              
              {/* Theme Name */}
              <span className="flex-1 text-left text-sm text-theme-primary">
                {t.icon} {t.name}
              </span>
              
              {/* Check mark for selected */}
              {theme === t.id && (
                <Check className="w-4 h-4 text-theme-accent" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ThemeSwitcher;
